import { AppTypedFilters, FilterTypeValue } from "@/components/filter/filter.types.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import useEmployeeSearchInline, {
  useEmployeesByIds,
} from "@/queries/employees/use-employees.query.ts";
import { FilterItem, Multiselect, SelectItem } from "@skailer-com/uikit";
import { useMemo } from "react";
import { Control, Controller, FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";

export const FilterEmployeeRender = ({
  item,
  control,
  errors,
  typedFilters,
  canViewEmployees,
}: {
  item: FilterItem<FilterTypeValue>;
  control: Control;
  errors: FieldErrors;
  typedFilters: AppTypedFilters;
  canViewEmployees: boolean;
}) => {
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();

  const typedValue = (typedFilters as Record<string, unknown>)[item.name];
  const currentIds = Array.isArray(typedValue) ? (typedValue as number[]) : [];

  const search = useEmployeeSearchInline(canViewEmployees);
  const { data: employeesLoaded } = useEmployeesByIds(currentIds, canViewEmployees);

  const employeesSearchTransformed: SelectItem[] = useMemo(() => {
    if (!employeesLoaded) return [];

    return employeesLoaded.map((employee) => ({
      id: employee.id,
      title: renderEmployeeName(employee),
    }));
  }, [employeesLoaded, renderEmployeeName]);

  if (!canViewEmployees) return null;

  return (
    <Controller
      key={item.name}
      render={({ field: { onChange, value } }) => (
        <Multiselect
          label={item.label ?? t("core:employees")}
          options={employeesSearchTransformed}
          values={value}
          onChange={onChange}
          searchOptions={search.options}
          showAvatar={true}
          showCaption={true}
          onSearchOptions={search.search}
          loadOptionsOnOpen
          searchable
          isClearable
          error={errors[item.name]?.message as string}
          isLoading={search.isLoading}
          emptyTitle={t("common:select")}
        />
      )}
      control={control}
      name={item.name}
    />
  );
};
